import { useEffect, useMemo, useState } from 'react';
import {
  ConsignmentApiModel,
  Country,
  Dimensions,
  InputRuleApiModel,
  PackageApiModel,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import { LocalUnitsEntry } from '~/apps/smartsend/lib/helpers/DimensionUnits';
import { DimensionsSelection } from '../DimensionsSelection';
import { ParcelArranger } from '../grouping/ParcelArranger';
import { bindParcelEdits, resolveRowDrag } from '~/apps/smartsend/lib/parcels/planEdits';
import { shouldRebuildFromOrder } from '~/apps/smartsend/lib/parcels/bounds';
import { SmartSendItemGroup } from '../item/SmartSendGroup';
import { ConsignmentOptionsAreAvailable } from './DimensionsAreValid';
import { ConsignmentSummary } from './consignments/ConsignmentSummary';
import { MultiConsignmentUnsupported } from './consignments/MultiConsignmentUnsupported';
import { GroupingPanel } from './GroupingPanel';
import { SettingsCache } from '~/lib/consts/settings-cache';
import {
  GenerateConsignmentFromItem,
  RedistributeConsignmentParcels
} from './consignments/GenerateConsignmentFromItem';
import { P2GBox, P2GButton, P2GText } from '@parcel2go/core-ui';
import { P2GSidePanel } from '~/components/sidepanel';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import ConsignmentSummaryParcels from './consignments/ConsignmentSummaryParcels';
import { SmartSendItemsToConsignment } from './consignments/SmartSendItemsToConsignment';
import { useHiddenRowFields } from './useHiddenRowFields';
import StickyButtons from '~/components/sticky';

export interface props {
  item: SmartSendItemApiModel;
  onUpdate: (c: ConsignmentApiModel) => void;
  onClose: () => void;
  dimensionsRequired?: boolean;
  hideDimensionsIfNotRequired: boolean;
  getPackages: () => Promise<PackageApiModel[]>;
  onDimensionsUpdate?: (dimensions: Dimensions, localUnitsEntry?: LocalUnitsEntry) => void;
  dimensions: Dimensions;
  getCountries?: () => Promise<Country[]>;
  getValidation?: () => Promise<{ ProductValidation: InputRuleApiModel[] }>;
  tariffSystem?: string;
  localUnitsEntry?: LocalUnitsEntry;
}

const ConsignmentDetailsProjection = ({
  item,
  hiddenRowFields,
  t
}: {
  item: SmartSendItemApiModel;
  hiddenRowFields: string[];
  t: any;
}) => {
  const c = useMemo(() => SmartSendItemsToConsignment([item]), [item]);
  // Deliberately NOT draggable. This is a projection of the eCommerce lines, not the stored parcel
  // plan - there is nothing here for a move to write back to.
  return (
    <ConsignmentSummaryParcels hiddenRowFields={hiddenRowFields} consignment={c} currencyCode={item.CurrencyCode}>
      <P2GBox mb={2}>
        <P2GText variant="caption">
          {t('CONSIGNMENT_ECOMMERCE_SUMMARY_CAPTION', {
            defaultValue: 'Shipment summary based on your eCommerce items:'
          })}
        </P2GText>
      </P2GBox>
    </ConsignmentSummaryParcels>
  );
};

export const ConsignmentDetails: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  // Only drives the "Parcel Contents" side panel now - deliberately does not default open on
  // mount (it used to double as an inline collapse/expand flag defaulting to IsInternational(item),
  // but reusing that for the panel's open state meant international items auto-opened the panel
  // as soon as this component mounted).
  const [expand, setExpand] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [productValidation, setProductValidation] = useState<InputRuleApiModel[]>([]);
  const serviceSupportsConsignments = SettingsCache.settings.Features.includes('Consignments');
  const hiddenRowFields = useHiddenRowFields(productValidation);

  useEffect(() => {
    props.getCountries?.().then(setCountries);
    props.getValidation?.().then((x) => setProductValidation(x?.ProductValidation || []));
  }, []);

  // props.dimensions stays in the deps so a weight typed into the single-parcel form below clears the
  // message live.
  const valid = useMemo(
    () => ConsignmentOptionsAreAvailable(props.item, props.dimensions, props.dimensionsRequired),
    [props.item, props.dimensions, props.dimensionsRequired]
  );

  const group = useMemo<SmartSendItemGroup>(() => {
    return { Items: [props.item], Title: '', IsGroupable: false, States: [], Hidden: false };
  }, [props.item]);

  const consignment = props.item.Consignment;
  const consignmentParcels = consignment?.Parcels?.length || 1;

  const splitConsignmentEnabled = SettingsCache.settings.Features.includes('ParcelSplitting');

  const onUpdate = (c: ConsignmentApiModel) => {
    if (props.onUpdate) {
      props.onUpdate(c);
    }
  };

  // The plan is owned by the item, not by a hook, so the edit actions read it straight off the item
  // and write back through onUpdate. `read` is a getter so each action sees the current consignment.
  const edits = useMemo(
    () =>
      bindParcelEdits(
        () => props.item.Consignment,
        onUpdate,
        () => props.dimensions
      ),
    [props.item, props.dimensions]
  );

  const sendAsSingle = () => {
    onUpdate(null);
    setExpand(false);
  };

  /**
   * A content row dragged from one parcel card onto another.
   *
   * Dragging everything into a single parcel is allowed and lands the item in the same state as picking
   * 1x - see RowDragOutcome for why that is not the same as a one-parcel consignment. A refused drop
   * writes nothing, so the item is not marked dirty.
   */
  const onDragRowToParcel = (contentUID: string, targetParcelUID: string) => {
    const outcome = resolveRowDrag(consignment, contentUID, targetParcelUID);

    if (outcome.kind === 'SEND_AS_SINGLE') {
      sendAsSingle();
    } else if (outcome.kind === 'PLAN') {
      onUpdate(outcome.plan);
    }
  };

  /**
   * Picking a box count. Redistribution preserves per-row edits but can only ever produce as many
   * parcels as the plan already has units, so a count above that is served by rebuilding from the
   * order - the bundled equivalent of picking 1x and then the new count, without the intermediate
   * clear (one state write and one requote rather than two).
   *
   * shouldRebuildFromOrder owns that decision and shares its pristine check with
   * consignmentParcelBound, so the counts offered and the route taken cannot disagree.
   */
  const generateConsignment = (qty: number) => {
    if (qty === 1) {
      sendAsSingle();
      return;
    }

    onUpdate(
      shouldRebuildFromOrder(consignment, props.item.MarketplaceOrderLines, qty)
        ? GenerateConsignmentFromItem(props.item, props.dimensions, qty)
        : RedistributeConsignmentParcels(props.item.Id, consignment, props.dimensions, qty)
    );
  };

  if (consignment && !serviceSupportsConsignments) {
    return <MultiConsignmentUnsupported reset={sendAsSingle} cancel={props.onClose} />;
  }

  return (
    <>
      <ConsignmentSummary
        serviceSupportsConsignments={serviceSupportsConsignments}
        consignment={consignment}
        consignmentParcels={consignmentParcels}
        setExpand={setExpand}
        group={group}
        valid={valid}
        generateConsignment={generateConsignment}
        hiddenRowFields={hiddenRowFields}
        onDragRowToParcel={onDragRowToParcel}
        splitConsignmentControl={
          splitConsignmentEnabled && (
            <GroupingPanel
              source="ITEM-PANEL"
              group={group}
              onComplete={props.onClose}
              countries={countries}
              productValidation={productValidation}
              tariffSystem={props.tariffSystem}
            />
          )
        }
      />
      {!consignment && (
        <>
          <P2GBox mt={4}>
            <ConsignmentDetailsProjection item={props.item} hiddenRowFields={hiddenRowFields} t={t} />
          </P2GBox>
          <P2GBox mt={4}>
            <DimensionsSelection
              startMinimised={false}
              dimensions={props.dimensions}
              localUnitsEntry={props.localUnitsEntry}
              onUpdate={props.onDimensionsUpdate}
              getPackages={props.getPackages}
              hideDimensionsIfNotRequired={props.hideDimensionsIfNotRequired}
              dimensionsRequired={props.dimensionsRequired}
            />
          </P2GBox>
        </>
      )}
      <P2GSidePanel
        anchor="right"
        open={!!consignment && expand}
        onClose={() => setExpand(false)}
        title={t('BOX_CONTENTS_PANEL_TITLE', { defaultValue: 'Box Contents' })}
        width="600px"
      >
        {!!consignment && expand && (
          // Controlled: the plan lives on item.Consignment, so edits are written straight back
          // through onUpdate. There is no remount key and no reseeding - the arranger previously
          // reseeded from this very prop after reporting up to it, a loop that only terminated
          // because the reported value reused the same array instance.
          <ParcelArranger
            plan={consignment}
            outcome="ONE_SHIPMENT"
            edit={edits}
            arrange={edits}
            canSplitRows
            perParcelDimensions={{
              required: props.dimensionsRequired,
              hideIfNotRequired: props.hideDimensionsIfNotRequired,
              getPackages: props.getPackages
            }}
            content={{
              countries,
              productValidation,
              tariffSystem: props.tariffSystem,
              sender: props.item.Sender,
              recipient: props.item.Recipient,
              currencyCode: props.item.CurrencyCode
            }}
            loadPreview={!expand}
          />
        )}
        <StickyButtons>
          <P2GBox mt={3}>
            <P2GButton variant="primary" onClick={() => setExpand(false)}>
              {t('PARCEL_CONTENTS_DONE_BUTTON', { defaultValue: 'Done' })}
            </P2GButton>
          </P2GBox>
        </StickyButtons>
      </P2GSidePanel>
    </>
  );
};
